/**
 * Paywall gate — present the RevenueCat-hosted paywall, then unlock the app once the License
 * flips to active.
 *
 * In-app subscriptions (2026-06-19, design §5). Flow on NATIVE:
 *   1. Present the RevenueCat paywall (presentPaywallIfNeeded — skips if "beam Pro" is already
 *      active, e.g. after a Restore on a fresh install).
 *   2. On a successful purchase/restore, poll GET /api/license until status becomes active
 *      (the RevenueCat → backend webhook grants the License keyed by org id), then enter /home.
 *   3. Offer Retry (re-present) and Restore purchases (Apple requirement).
 *
 * On WEB this screen never presents a native paywall (the platform-split stub makes the calls
 * no-ops); it points the user at the web (Stripe) checkout instead. The AuthGate only routes
 * native locked store/incomplete accounts here.
 *
 * Built on the design system. The License read is a real round-trip via TanStack Query with
 * loading / error states.
 */
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { getLicense } from '../src/api/account';
import { useSession } from '../src/auth/session';
import {
  REVENUECAT_AVAILABLE,
  presentPaywallIfNeeded,
  restorePurchases,
} from '../src/billing/revenuecat';
import { openExternalUrl } from '../src/platform/linking';
import { Screen, Text, Card, Button } from '../src/components/ui';
import { BeamLogo } from '../src/components/brand/Logo';
import { theme } from '../src/theme';

const IS_WEB = Platform.OS === 'web';
// Web (Stripe) checkout lives on the marketing site; native users never see it (Apple/Google rules).
const WEB_PRICING_URL = 'https://getabeam.com/pricing';

type Phase = 'idle' | 'presenting' | 'polling' | 'unlocked' | 'cancelled' | 'error';

// Poll /api/license after a purchase until the webhook activates the license (or we give up).
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // ~30s

export default function PaywallScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const logout = useSession((s) => s.logout);

  const [phase, setPhase] = useState<Phase>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  const proceedIntoApp = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['license'] });
    router.replace('/home');
  }, [queryClient, router]);

  const pollUntilActive = useCallback(async (): Promise<boolean> => {
    setPhase('polling');
    for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
      try {
        const license = await getLicense();
        if (license.active) return true;
      } catch {
        // transient — keep polling
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    return false;
  }, []);

  const runPaywall = useCallback(async () => {
    if (!REVENUECAT_AVAILABLE) {
      // Web — no native paywall. Already covered by the render branch below.
      return;
    }
    setMessage(null);
    setPhase('presenting');
    const outcome = await presentPaywallIfNeeded();

    if (outcome === 'purchased' || outcome === 'restored' || outcome === 'not_presented') {
      // not_presented means "beam Pro" is already active for this purchaser — treat as success
      // and let the license poll confirm the backend caught up.
      const active = await pollUntilActive();
      if (active) {
        setPhase('unlocked');
        proceedIntoApp();
        return;
      }
      setPhase('error');
      setMessage(
        'Your purchase went through but your account is still activating. This can take a moment. Tap Retry.',
      );
      return;
    }

    if (outcome === 'cancelled') {
      setPhase('cancelled');
      setMessage('A subscription is required to use Beam. Subscribe whenever you are ready to unlock it.');
      return;
    }

    setPhase('error');
    setMessage('Something went wrong opening the subscription screen. Please try again.');
  }, [pollUntilActive, proceedIntoApp]);

  // Auto-present on first mount (native only).
  useEffect(() => {
    if (REVENUECAT_AVAILABLE && phase === 'idle') {
      void runPaywall();
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRestore() {
    setRestoring(true);
    setMessage(null);
    try {
      const { ok, subscribed } = await restorePurchases();
      if (!ok) {
        setMessage('Could not restore purchases. Please try again.');
        return;
      }
      if (!subscribed) {
        setMessage('No active subscription was found to restore.');
        return;
      }
      const active = await pollUntilActive();
      if (active) {
        setPhase('unlocked');
        proceedIntoApp();
      } else {
        setMessage('Subscription restored — activating your account. Tap Retry in a moment.');
      }
    } finally {
      setRestoring(false);
    }
  }

  // --- Web: no native paywall; route to Stripe checkout -------------------------------------
  if (IS_WEB || !REVENUECAT_AVAILABLE) {
    return (
      <Screen scroll>
        <View style={styles.center}>
          <BeamLogo size={40} />
          <Card style={styles.card} testID="paywall-screen">
            <Text variant="title">Choose your plan</Text>
            <Text variant="body" color="secondary">
              A subscription is required to use Beam. Subscribe on the web to unlock it, then sign
              in from any device once active.
            </Text>
            <Button
              title="See pricing"
              variant="primary"
              size="lg"
              onPress={() => void openExternalUrl(WEB_PRICING_URL)}
              accessibilityLabel="See pricing"
            />
            <Button
              title="Sign out"
              variant="ghost"
              onPress={() => void logout()}
              accessibilityLabel="Sign out"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  // --- Native --------------------------------------------------------------------------------
  const busy = phase === 'presenting' || phase === 'polling';

  return (
    <Screen scroll>
      <View style={styles.center}>
        <BeamLogo size={40} />
        <Card style={styles.card} testID="paywall-screen">
          <Text variant="title">Unlock Beam</Text>
          <Text variant="body" color="secondary">
            A subscription is required to use Beam. The Crew plan runs your jobs, estimates,
            invoices, and reporting.
          </Text>

          {busy ? (
            <View style={styles.busyRow}>
              <ActivityIndicator accessibilityLabel="Working" />
              <Text variant="body" color="secondary">
                {phase === 'polling' ? 'Activating your account…' : 'Opening subscription…'}
              </Text>
            </View>
          ) : null}

          {message ? (
            <Text
              variant="body"
              color={phase === 'error' ? 'danger' : 'secondary'}
              accessibilityRole="alert"
            >
              {message}
            </Text>
          ) : null}

          {!busy ? (
            <>
              <Button
                title={phase === 'cancelled' || phase === 'error' ? 'View plans' : 'Subscribe'}
                variant="primary"
                size="lg"
                onPress={() => void runPaywall()}
                testID="paywall-subscribe"
                accessibilityLabel="View subscription plans"
              />
              <Button
                title="Restore purchases"
                variant="ghost"
                onPress={() => void handleRestore()}
                loading={restoring}
                disabled={restoring}
                accessibilityLabel="Restore purchases"
              />
              <Button
                title="Sign out"
                variant="ghost"
                onPress={() => void logout()}
                accessibilityLabel="Sign out"
              />
            </>
          ) : null}

          <Text variant="small" color="secondary" style={styles.fine}>
            Subscriptions renew automatically until cancelled. Manage or cancel anytime in your
            account. Terms and Privacy Policy apply.
          </Text>
        </Card>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing['3xl'] },
  card: { width: '100%', maxWidth: 440, gap: theme.spacing.md },
  busyRow: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
  fine: { marginTop: theme.spacing.sm },
});
